import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { apiClient, type AdminUserDetail, type TempDefaults } from '@/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import { durationToWire, type ExpiryUnit } from '@/lib/tempAccount';

function parseLimit(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n > 0 ? n : NaN;
}

export default function CreateUserDialog({
  open,
  onClose,
  onCreated
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (detail: AdminUserDetail) => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [admin, setAdmin] = useState(false);
  const [temporary, setTemporary] = useState(false);
  const [expireValue, setExpireValue] = useState('');
  const [expireUnit, setExpireUnit] = useState<ExpiryUnit>('days');
  const [inputTokens, setInputTokens] = useState('');
  const [outputTokens, setOutputTokens] = useState('');
  const [defaults, setDefaults] = useState<TempDefaults>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUsername('');
    setPassword('');
    setEmail('');
    setAdmin(false);
    setTemporary(false);
    setBusy(false);
    apiClient
      .listPendingSignups()
      .then((r) => {
        const d = r.temp_defaults ?? {};
        setDefaults(d);
        setExpireValue(d.days != null ? String(d.days) : '');
        setExpireUnit('days');
        setInputTokens(d.input_tokens != null ? String(d.input_tokens) : '');
        setOutputTokens(d.output_tokens != null ? String(d.output_tokens) : '');
      })
      .catch(() => setDefaults({}));
  }, [open]);

  const submit = async () => {
    if (!username.trim() || username.includes('@')) {
      toast.error("Username is required and cannot contain '@'.");
      return;
    }
    if (!password.trim()) {
      toast.error('Password is required.');
      return;
    }
    let temp = { temporary: false } as {
      temporary: boolean;
      day_limit?: number | null;
      input_token_limit?: number | null;
      output_token_limit?: number | null;
      expire_in_minutes?: number | null;
    };
    if (temporary) {
      const inputLimit = parseLimit(inputTokens);
      const outputLimit = parseLimit(outputTokens);
      const duration = durationToWire(expireValue, expireUnit);
      if (duration == null || Number.isNaN(inputLimit) || Number.isNaN(outputLimit)) {
        toast.error('Limits must be whole numbers greater than zero, or left blank.');
        return;
      }
      temp = {
        temporary: true,
        day_limit: duration.day_limit,
        expire_in_minutes: duration.expire_in_minutes,
        input_token_limit: inputLimit,
        output_token_limit: outputLimit
      };
    }
    setBusy(true);
    try {
      const detail = await apiClient.createAdminUser({
        username: username.trim(),
        password,
        email: email.trim() || null,
        role: admin ? 'admin' : 'user',
        ...temp
      });
      toast.success(`Created ${detail.identifier}`);
      onCreated(detail);
      onClose();
    } catch (error: any) {
      console.error('Create user failed', error);
      toast.error(error?.message || 'Create user failed');
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Create user</AlertDialogTitle>
          <AlertDialogDescription>
            Provision a credential account. The user signs in with the username and password you
            set here.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Username (no @)</span>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="operator1" />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Password</span>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="set a password" />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Email (optional)</span>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium">Admin role</span>
            <Switch
              checked={admin}
              onCheckedChange={(v) => {
                setAdmin(v);
                if (v) setTemporary(false);
              }}
            />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium">Temporary account</span>
            <Switch
              checked={temporary}
              disabled={admin}
              onCheckedChange={(v) => {
                setTemporary(v);
                if (v) setAdmin(false);
              }}
            />
          </label>
          {admin ? (
            <p className="text-xs text-muted-foreground">
              Admin accounts can't be temporary.
            </p>
          ) : null}
          {temporary && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Blank = not enforced. Defaults shown from {defaults.days != null ? 'config' : 'none'}. Access is
                blocked when either token cap is reached; the expiry clock starts now.
              </p>
              <div className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Expire after</span>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={1}
                    className="flex-1"
                    value={expireValue}
                    onChange={(e) => setExpireValue(e.target.value)}
                    placeholder="not enforced"
                  />
                  <Select value={expireUnit} onValueChange={(v) => setExpireUnit(v as ExpiryUnit)}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Input token limit</span>
                <Input
                  type="number"
                  min={1}
                  value={inputTokens}
                  onChange={(e) => setInputTokens(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Output token limit</span>
                <Input
                  type="number"
                  min={1}
                  value={outputTokens}
                  onChange={(e) => setOutputTokens(e.target.value)}
                />
              </label>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            Create
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
